/**
 * Discriminators for Vuex `listNavigation` sequence entries.
 * Add new string literals when wiring other list → detail flows (e.g. tenders).
 */
export const LIST_NAV_KIND = {
  HOTB: 'hotb',
  TENANT_PROJECT: 'tenantProject',
  /** All tenancy records list → TenancyView detail */
  TENANCY_RECORD: 'tenancyRecord',
  /** Engineer View All projects → ProjectDetail (`/projects/:id`) */
  PROJECT: 'project',
  /** Tender View All (/tenders) → tender detail (summary subtree or flat tender routes) */
  TENDER_RECORD: 'tenderRecord',
  /** Work Permit Dashboard → project/tenant-project/standalone work permit detail */
  WORK_PERMIT: 'workPermit',
}

/**
 * Normalize one navigation entry from store/API/mixin shapes.
 * Prefer `kind`; legacy `{ id, isHotb }` is still accepted.
 *
 * @param {object} entry
 * @returns {string}
 */
export function resolveNavigationKind (entry) {
  if (!entry || entry.kind === null || entry.kind === undefined) {
    if (typeof entry.isHotb === 'boolean') {
      return entry.isHotb ? LIST_NAV_KIND.HOTB : LIST_NAV_KIND.TENANT_PROJECT
    }
    return LIST_NAV_KIND.TENANT_PROJECT
  }
  const k = String(entry.kind).trim()
  return k.length ? k : LIST_NAV_KIND.TENANT_PROJECT
}

/**
 * Trimmed `entry.routeName` when present on a sequence row (e.g. tenders).
 * @param {object} entry
 * @returns {string}
 */
export function getListNavigationEntryRouteName (entry) {
  if (!entry || entry.routeName == null) return ''
  return String(entry.routeName).trim()
}

/**
 * Builds a vue-router location for `{ name, params }` when the row carries `routeName`
 * (e.g. Tender prev/next preserves `prev` like the originating list route).
 *
 * @param {object} entry `{ id, routeName?, ... }`
 * @param {string|undefined|null} [routePrevParam] current `$route.params.prev` when carrying context forward
 * @returns {{ name: string, params: { id: string, prev?: string } } | null}
 */
export function buildListNavigationNamedRouteLocation (entry, routePrevParam = undefined) {
  if (!entry || entry.id == null || entry.id === '') return null
  const name = getListNavigationEntryRouteName(entry)
  if (!name) return null
  const entryParams = entry.params && typeof entry.params === 'object' ? entry.params : null
  const params = entryParams ? { ...entryParams } : { id: String(entry.id) }
  if (params.id != null) params.id = String(params.id)
  if (routePrevParam !== undefined && routePrevParam !== null && String(routePrevParam) !== '') {
    params.prev = params.prev || routePrevParam
  }
  const loc = { name, params }
  if (entry.query && typeof entry.query === 'object') {
    loc.query = { ...entry.query }
  }
  return loc
}

/**
 * Merges HOTB / tenant-project path defaults (same logic as `HKLHeader` computed `mergedNavPaths`).
 */
export function mergeListNavigationHeaderPaths (
  navPathsByKind = {},
  navBasePathHotb = '',
  navBasePathTenantProject = '',
) {
  const out = { ...navPathsByKind }
  const hotb = String(navBasePathHotb || '').trim()
  const tp = String(navBasePathTenantProject || '').trim()
  if (hotb && out[LIST_NAV_KIND.HOTB] == null) out[LIST_NAV_KIND.HOTB] = hotb
  if (tp && out[LIST_NAV_KIND.TENANT_PROJECT] == null) out[LIST_NAV_KIND.TENANT_PROJECT] = tp
  return out
}

/**
 * Navigate to another list-navigation row (`routeName` or `kind` + merged path prefixes).
 * Use from parent `@right-leading` / `@right-trailing` handlers (HKLHeader only emits).
 *
 * @param {*} router Vue Router instance (`this.$router`)
 * @param {*} route Current route (`this.$route`)
 * @param {Record<string, string>} mergedNavPaths From {@link mergeListNavigationHeaderPaths}
 * @param {object|null} entry Payload from `emitNavPayload` / store row shape
 */
export function pushListNavigationEntry (router, route, mergedNavPaths, entry) {
  if (!router || !route || !entry || entry.id == null || entry.id === '') return
  const prevParam = route.params && route.params.prev
  const namedLoc = buildListNavigationNamedRouteLocation(entry, prevParam)
  if (namedLoc) {
    const resolved = router.resolve(namedLoc)
    const resolvedFullPath = (resolved.route && resolved.route.fullPath) || (resolved.route && resolved.route.path)
    const currentFullPath = route.fullPath || route.path
    if (resolvedFullPath !== currentFullPath) {
      router.push(namedLoc)
    }
    return
  }
  const kind = resolveNavigationKind(entry)
  const base = String((mergedNavPaths && mergedNavPaths[kind]) || '').replace(/\/$/, '')
  if (!base) return
  const idEnc = encodeURIComponent(String(entry.id))
  const path = `${base}/${idEnc}`
  if (path !== route.path) {
    router.push(path)
  }
}
